import React, { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  CardElement,
  Elements,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  createPaymentIntent,
  ensureMembershipStripeCustomer,
  recordCardInvoicePayment,
} from "../../../api/paymentsAPI";
import { getUserDetail } from "../../../api/usersAPI";
import {
  selectMembershipDetails,
  setMembership,
} from "../../../store/slices/membershipSlice";
import { useTheme } from "../../../context/ThemeContext";
import {
  getSecondaryText,
  getLightBorder
} from "../../../utils/themeUtils";
import { getStripePromise } from "../../../utils/stripeClient";
import { captureError } from "../../../utils/sentry";
import { isPaymentsUnavailable, PAYMENTS_UNAVAILABLE_MESSAGE } from "../../../utils/paymentErrors";
import { useLeadBillingPrompt, withLeadBillingChoice } from "../../../hooks/useLeadBillingPrompt";
import type { LeadBillingDecision } from "../../../hooks/useLeadBillingPrompt";

/** The server said this club cannot take card payments (503 PAYMENTS_UNAVAILABLE). */
class PaymentsUnavailableError extends Error {
  constructor() {
    super(PAYMENTS_UNAVAILABLE_MESSAGE);
    this.name = "PaymentsUnavailableError";
    // Keeps `instanceof` true when compiled to ES5 classes.
    Object.setPrototypeOf(this, PaymentsUnavailableError.prototype);
  }
}

/** Shown when there is no amount to charge. Nothing is charged. */
export const NO_AMOUNT_MESSAGE = "Enter the amount to charge before paying. Nothing has been charged.";

/** Dollars as typed -> cents, or null when there is no positive amount. */
export const amountToCents = (amount: string | number | null | undefined): number | null => {
  if (amount === "" || amount == null) return null;
  const n = typeof amount === "number" ? amount : parseFloat(String(amount));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
};

const AddNewCard: React.FC<{
  amount: string;
  invoice: any;
  onClose: any;
  fetch: any;
  /**
   * What staff already chose for a lead earlier in this same attempt (Create &
   * Pay asks before it creates the invoice). Left out, this form asks itself.
   */
  leadBilling?: LeadBillingDecision | null;
}> = ({ amount, invoice, onClose, fetch, leadBilling }) => {
  const { isDarkMode } = useTheme();
  // A lead's account: ask once before the card is charged (never blocks).
  const { confirmLeadBilling, leadBillingPrompt } = useLeadBillingPrompt();
  const membership: any = useSelector(selectMembershipDetails);
  const [loading, setLoading] = useState(false);
  const [memberDetails, setMemberDetails] = useState<any>(null);
  const stripe = useStripe();
  const elements = useElements();
  // HELPER
  const token = localStorage.getItem("authToken");
  const dispatch = useDispatch();

  // Resolve a Stripe customer for this membership: reuse existing if present,
  // otherwise ask the server for the account's one. Returns the id or throws.
  // ROOT CAUSE FIX (2026-04-28): always reuse membership.stripe_customer_id
  // when present so Stripe Radar sees the customer's prior history. Brand-new
  // empty Stripe customers get declined far more often than ones with a track
  // record.
  const resolveStripeCustomerId = async (): Promise<string> => {
    if (membership?.stripe_customer_id) {
      return membership.stripe_customer_id;
    }
    // The SERVER finds or makes the account's payment profile and saves it on
    // the account. This used to make one through the public sign-up route and
    // then save its id on the membership from here; the membership save no
    // longer accepts that field, so the id was being lost.
    const membershipId = invoice?.membership_id || membership?.id;
    if (!membershipId) {
      throw new Error("No membership to take a payment for.");
    }
    const customerResponse = await ensureMembershipStripeCustomer(token, membershipId);
    // controller returns { success, data: { id } } so id is at .data.data.id
    const customerId = customerResponse?.data?.data?.id || null;
    if (!customerId) {
      // A club with no card processor connected: say that, not "try again".
      if (isPaymentsUnavailable(customerResponse?.data)) {
        throw new PaymentsUnavailableError();
      }
      throw new Error("Stripe customer creation failed.");
    }
    // Keep this screen's copy of the account in step with what the server saved.
    if (membership?.id && membership.id === membershipId) {
      dispatch(setMembership({ ...membership, stripe_customer_id: customerId }));
    }
    return customerId;
  };

  // handle form submit. Customer + PaymentIntent are created on demand here;
  // nothing happens against Stripe until the user actually clicks Pay.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      toast.error("Payment system is still loading. Please refresh and try again.");
      captureError(new Error("AddNewCard: stripe/elements null at submit"), {
        component: "AddNewCard",
        operation: "stripeNotReady",
      });
      return;
    }

    // The amount to charge must be a real, positive amount. An empty one used
    // to become $25.00 (2026-10-09): a guess is never charged to a card.
    const amountInCents = amountToCents(amount);
    if (amountInCents == null) {
      toast.error(NO_AMOUNT_MESSAGE);
      return;
    }

    const payingFor = invoice?.membership_id || membership?.id;
    const leadDecision = await confirmLeadBilling({
      membershipId: payingFor,
      accountType: membership?.id && membership.id === payingFor ? membership.account_type : undefined,
      action: "payment",
      decided: leadBilling,
    });
    if (!leadDecision.proceed) return;

    setLoading(true);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error("Card Element not found.");
      setLoading(false);
      return;
    }

    // 1. Resolve customer + create PaymentIntent on the backend
    let stripeCustomerId: string;
    let clientSecret: string;
    try {
      stripeCustomerId = await resolveStripeCustomerId();
      const intentResponse = await createPaymentIntent({
        amount: amountInCents,
        customerId: stripeCustomerId,
      });
      clientSecret = intentResponse?.data?.clientSecret;
      if (!clientSecret) throw new Error("PaymentIntent creation failed.");
    } catch (err) {
      if (err instanceof PaymentsUnavailableError) {
        // A known state of the club, not a fault: nothing to report.
        toast.error(PAYMENTS_UNAVAILABLE_MESSAGE);
        setLoading(false);
        return;
      }
      captureError(err, { component: "AddNewCard", operation: "createIntent" });
      toast.error("Could not initialize payment. Please try again.");
      setLoading(false);
      return;
    }

    // 2. Create a payment method
    const { error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error) {
      toast.error(error.message || "Failed to create card.");
      setLoading(false);
      return;
    } else {
      // Confirm the payment using the card details and client secret
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement!,
            billing_details: {
              // Optional-chained: this threw if the member fetch had not resolved
              // (or failed) by the time the form was submitted — a crash INSIDE the
              // card handler, before any charge, with no error path.
              name: [memberDetails?.first_name, memberDetails?.last_name]
                .filter(Boolean)
                .join(" "),
            },
          },
        }
      );

      if (confirmError) {
        // Surface the actual Stripe decline / authentication / processing
        // error to the user instead of silently swallowing it. Previously
        // this branch did nothing and the modal closed with no feedback,
        // making it look like "the form didn't work".
        captureError(confirmError, {
          component: "AddNewCard",
          operation: "confirmCardPayment",
          stripeErrorCode: confirmError.code,
          stripeDeclineCode: confirmError.decline_code,
        });
        toast.error(
          confirmError.message ||
            "Card was declined. Please try a different card."
        );
        setLoading(false);
        return;
      } else if (paymentIntent?.status !== "succeeded") {
        // PaymentIntent didn't succeed but Stripe didn't surface an error
        // (e.g. requires_action that couldn't be completed). Tell the user.
        captureError(
          new Error(`PaymentIntent status: ${paymentIntent?.status}`),
          {
            component: "AddNewCard",
            operation: "confirmCardPayment",
            paymentIntentId: paymentIntent?.id,
            paymentIntentStatus: paymentIntent?.status,
          }
        );
        toast.error(
          `Payment did not complete (status: ${paymentIntent?.status || "unknown"}). Please try again or use a different card.`
        );
        setLoading(false);
        return;
      } else if (paymentIntent?.status === "succeeded") {
        // THE CARD HAS BEEN CHARGED. Everything below is bookkeeping, and it
        // was previously unprotected. A failure here loses the record of
        // money that has already moved.
        try {
          // ONE request, and its answer is read. The server asks the card
          // processor what was paid, records the payment and its credit, and
          // marks the invoice paid (POST /payments/invoice-card-payment).
          //
          // This used to be four calls from here: create a second invoice
          // ("Charged Payment for invoice ..."), record a payment row against
          // it, mark it paid, mark the original paid. The answers to the last
          // two were thrown away. Creating and editing an invoice is
          // staff-only, so for a family the card was charged and nothing was
          // recorded; for staff the second invoice was a CHARGE, so the
          // family's balance counted the invoice twice and the payment not at
          // all.
          const recordRes: any = await recordCardInvoicePayment(token, withLeadBillingChoice({
            invoiceId: invoice?.id,
            paymentIntentId: paymentIntent?.id,
          }, leadDecision));
          const recorded = recordRes?.data;
          if (recorded?.success !== true) {
            throw new Error(recorded?.error || "Payment succeeded but could not be recorded.");
          }

          if (recorded?.data?.invoiceMarked === false) {
            // Recorded on the account; only the invoice's "paid" label is behind.
            toast.success("Payment successful!");
            toast.warning(
              "Your payment is on the account, but this invoice may still show as unpaid for a moment. " +
                "You do not need to pay again.",
              { autoClose: false }
            );
          } else {
            toast.success("Payment successful!");
          }
        } catch (bookkeepingErr: any) {
          // The card WAS charged. Say so plainly — telling the member the
          // payment failed would invite a second charge.
          captureError(bookkeepingErr, {
            component: "AddNewCard",
            operation: "recordPaymentAfterCharge",
            paymentIntentId: paymentIntent?.id,
            invoiceId: invoice?.id,
            severity: "money",
          });
          toast.error(
            "Your card was charged, but we couldn't finish recording it. " +
              "Please contact the club before paying again so it isn't charged twice."
          );
          setLoading(false);
          return;
        }
      }
    }

    setLoading(false);
    // Re
    fetch();
    onClose();
  };

  useEffect(() => {
    const fetchUser = async () => {
      const res = await getUserDetail(token, invoice?.membership_id);
      if (res?.status === 200) {
        setMemberDetails(res?.data?.user);
      }
    };
    fetchUser().catch((err) => {
      // fetchUser is a bare async function with no try/catch of its own —
      // an unguarded rejection here is unhandled and the user panel just
      // renders stale/empty.
      captureError(err, { component: "AddNewCard", operation: "fetchUser" }, "warning");
    });
  }, []);

  return (
    <div>
      {/*  */}
      <form onSubmit={handleSubmit}>
        <div>
          <label className={`block text-sm font-semibold ${getSecondaryText(isDarkMode)}`}>
            Credit Card Number *
          </label>
          <div className={`mt-2 w-full p-3 border rounded-md ${getLightBorder(isDarkMode)}`}>
            <CardElement />
          </div>
        </div>
        {/* Submit Button */}
        <div className="text-left mt-2">
          <button
            type="submit"
            className="bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] px-6 py-3 rounded-md font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!stripe || loading}
          >
            {loading ? (
              <div className="loader"></div>
            ) : (
              "Update Card"
            )}
          </button>
        </div>
      </form>
      {leadBillingPrompt}
    </div>
  );
};

// export default AddNewCard;

// Wrapper to load Stripe Elements
const StripeWrapper = (props: any) => {
  return (
    <Elements stripe={getStripePromise()}>
      <AddNewCard {...props} />
    </Elements>
  );
};

export default StripeWrapper;
